import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Stepper, Step, StepLabel, Checkbox, Stack, Box, Paper, Typography, Fade,
  CircularProgress,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { styled } from "@mui/material/styles";
import {
  Building2,
  Eye,
  Home,
  IdCard,
  Map,
  Layers,
  UserRoundPlus,
  WalletCards,
} from "lucide-react";
import PropTypes from "prop-types";
import { FRONTEND_MESSAGES } from "../../utils/messages";
import * as yup from "yup";

import CommonStep from "./CommonStep";
import { stepperFormScopeSx } from "./stepperFormStyles";
import "./TenantStepper.css";
import CustomSnackbar from "../Common/snackBar";
import { MEDIA_URL, PAYCENTRAL_MEDIA_URL } from "../../config";
import TenantInformationStep from "./TenantInformationStep";
import TenantPlanStructureStep from "./TenantPlanStructureStep";
import CostInfoStep from "../Organization/CostInfoStep";
import OrganizationInfoStep from "../Organization/OrganizationInfoStep";
import TenantCreationPreview from "./TenantCreationPreview";
import { getStateAction } from "../../Actions/SettingAction";
import {
  createTenantAction,
  editTenantAction,
  getCountriesDataAction,
  getTenantCurrenciesAction,
  getTenantDetailByIdAction,
  getAllTenantDetailByIdAction,
  getTimeZone,
} from "../../Actions/TenantActions";
import { getUserData } from "../../Actions/common";
import { verifyDomain, getBusinessType } from "../../Actions/OrganizationAction";
import KycInfoStep from "../KYC";
import { listCountriesAction, listAllKycPlansDropDownAction } from "../../Actions/KycPlanManagemantAction";
import { listTripolaPlansForDropdownAction } from "../../Actions/TripolaPlanActions";
import TripolaInformationStep from "../Tripola/TripolaInformationStep";
import TripolaPlanStructureStep from "../Tripola/TripolaPlanStructureStep";
import { containsBadWords, containsBadWordsInEmail } from "../../utils/badWord";

const ColorlibStepIconRoot = styled("div")(({ theme }) => ({
  backgroundColor: "#ccc",
  zIndex: 1,
  color: "#fff",
  width: 50,
  height: 50,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  ...theme.applyStyles("dark", {
    backgroundColor: theme.palette.grey[700],
  }),
  variants: [
    {
      props: ({ ownerState }) => ownerState.active,
      style: {
        backgroundImage:
          "linear-gradient( 136deg, #22419e 0%, #22419e 50%,#22419e 100%)",
        boxShadow: "0 4px 10px 0 rgba(0,0,0,.25)",
      },
    },
    {
      props: ({ ownerState }) => ownerState.completed,
      style: {
        backgroundImage:
          "linear-gradient( 136deg, #22419e 0%, #22419e 50%,#22419e 100%)",
      },
    },
  ],
}));

const STEP_ICON_MAP = {
  "Basic Info": UserRoundPlus,
  "Qbot Information": Home,
  "Qbot Plan Structure": WalletCards,
  "Corporation Information": Building2,
  "Corporation Plan Structure": Layers,
  "KYC Information": IdCard,
  "Tripola Information": Map,
  "Tripola Plan Structure": WalletCards,
  "Qbot Preview": Eye,
  "Corporation Preview": Eye,
  "KYC Preview": Eye,
  "Tripola Preview": Eye,
};

function ColorlibStepIcon(props) {
  const { active, completed, className, icon, steps } = props;

  const stepLabel = steps?.[Number(icon) - 1];
  const IconComponent = STEP_ICON_MAP[stepLabel] || UserRoundPlus;

  return (
    <ColorlibStepIconRoot
      ownerState={{ completed, active }}
      className={className}
    >
      <IconComponent size={22} strokeWidth={2} color="#ffffff" aria-hidden />
    </ColorlibStepIconRoot>
  );
}

ColorlibStepIcon.propTypes = {
  active: PropTypes.bool,
  className: PropTypes.string,
  completed: PropTypes.bool,
  icon: PropTypes.node,
};

const { VALIDATION } = FRONTEND_MESSAGES;

const merchantInformationObject = {
  companyName: yup
    .string()
    .trim()
    .required(VALIDATION.REQUIRED_FIELD("Company name"))
    .min(1, VALIDATION.MIN_LENGTH("Company name", 1))
    .max(160, VALIDATION.MAX_LENGTH("Company name", 160))
    .test(
      "bad-word",
      "Company name contains inappropriate language.",
      (value) => !containsBadWords(value || "")
    )
    .matches(/^[a-zA-Z0-9_\-'&\s\u2018\u2019\u2018\u0060\u2013\u2014]+$/, VALIDATION.INVALID_FORMAT("Company name")),
  currency: yup.string().trim().required(VALIDATION.SELECT_OPTION("Currency")),
  companyEmail: yup
    .string()
    .trim()
    .required(VALIDATION.REQUIRED_FIELD("Company email"))
    .email(VALIDATION.INVALID_EMAIL)
    .min(1, VALIDATION.MIN_LENGTH("Company email", 1))
    .max(70, VALIDATION.MAX_LENGTH("Company email", 70))
    .test(
      "bad-email",
      "Company email contains inappropriate language.",
      (value) => !containsBadWordsInEmail(value || "")
    )
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, VALIDATION.INVALID_EMAIL),
  companyZipCode: yup
    .string()
    .trim()
    .required(VALIDATION.REQUIRED_FIELD("Company zip code"))
    .min(4, VALIDATION.MIN_LENGTH("Company zip code", 4))
    .max(12, VALIDATION.MAX_LENGTH("Company zip code", 12))
    .matches(/^[a-zA-Z0-9\-\u2013\u2014]+$/, VALIDATION.INVALID_FORMAT("Company zip code"))
    .test("not-all-zeros", VALIDATION.INVALID_FORMAT("Company zip code"), (value) => {
      if (!value) return true;
      return !/^0+$/.test(value.replace(/[\-\u2013\u2014]/g, ''));
    }),
  companyPhoneNumber: yup
    .string()
    .required(VALIDATION.REQUIRED_FIELD("Company phone number"))
    .min(5, VALIDATION.MIN_LENGTH("Company phone number", 5))
    .max(15, VALIDATION.MAX_LENGTH("Company phone number", 15)),
  brandingName: yup
    .string()
    .max(10, VALIDATION.MAX_LENGTH("Branding Name", 10))
    .test(
      "bad-word",
      "Branding Name contains inappropriate language.",
      (value) => !containsBadWords(value || "")
    ),
  domainUrl: yup.string().required(VALIDATION.REQUIRED_FIELD("Domain URL"))
    .test(
      "bad-word",
      "Domain URL contains inappropriate language.",
      (value) => !containsBadWords(value || "")
    ),
  isVerified: yup
    .boolean()
    .oneOf([true], VALIDATION.INVALID_FORMAT("Domain verification"))
    .required(VALIDATION.REQUIRED_FIELD("Domain verification")),
  whitelistIps: yup.array().of(yup.string()).nullable(),
  brandingLogo: Yup.mixed()
    .required("Please select the branding Logo.")
    .test(
      "fileType",
      "Only JPEG, PNG and JPG files are allowed",
      (value) => {
        if (!value) {
          return true;
        }

        const isFile = value instanceof File;
        if (!isFile) {
          return true;
        }
        if (value) {
          const supportedFormats = ["jpeg", "png", "jpg"];
          return supportedFormats.includes(
            value.name.split(".").pop()
          );
        }
        return true;
      }
    )
    .test("fileSize", "File size should be less than 2 MB", (value) => {
      const maxFileSize = 2 * 1024 * 1024; // 2 MB
      if (!value) {
        return true;
      }

      const isFile = value instanceof File;
      if (!isFile) {
        return true;
      }

      return value.size <= maxFileSize;
    }),
};

const validationMerchantInformationSchema = yup.object(
  merchantInformationObject
);

const validationPlanStructureSchema = yup.object({
  planId: yup.mixed().required(VALIDATION.REQUIRED_FIELD("Plan")),
  billingCycle: yup
    .string()
    .oneOf(["monthly", "yearly"], VALIDATION.INVALID_FORMAT("Billing cycle"))
    .required(VALIDATION.REQUIRED_FIELD("Billing cycle")),
  paymentMode: yup
    .string()
    .oneOf(["online", "offline"], VALIDATION.INVALID_FORMAT("Payment mode"))
    .required(VALIDATION.REQUIRED_FIELD("Payment mode")),
  isAutoPay: yup.string(),
  hideBillingDetails: yup.boolean(),
});

const organizationValidationSchema = Yup.object({
  organizationLogo: Yup.mixed()
    .required("Please select the Corporation Logo.")
    .test(
      "fileType",
      "Only JPEG, PNG and JPG files are allowed",
      (value) => {
        if (!value) {
          return true;
        }

        const isFile = value instanceof File;
        if (!isFile) {
          return true;
        }
        if (value) {
          const supportedFormats = ["jpeg", "png", "jpg"];
          return supportedFormats.includes(
            value.name.split(".").pop()
          );
        }
        return true;
      }
    )
    .test("fileSize", "File size should be less than 2 MB", (value) => {
      const maxFileSize = 2 * 1024 * 1024; // 2 MB
      if (!value) {
        return true;
      }

      const isFile = value instanceof File;
      if (!isFile) {
        return true;
      }

      return value.size <= maxFileSize;
    }),
  organizationName: Yup.string().required(
    "Please enter the Corporation Name."
  )
    .test(
      "bad-word",
      "Organizationname contains inappropriate language.",
      (value) => !containsBadWords(value || "")
    ),
  userName: Yup.string()
    .trim()
    .test(
      "bad-word",
      "Username contains inappropriate language.",
      (value) => !containsBadWords(value || "")
    )
    .matches(
      /^[a-zA-Z0-9_ ]*$/,
      "Username must not contain special characters."
    )
    .required("Please enter the userName"),
  isVerified: Yup.boolean().oneOf(
    [true],
    "Please verify before proceeding."
  ),
  isAutoInvoice: Yup.boolean(),
  businessName: Yup.string()
    .trim()
    .test(
      "bad-word",
      "Business contains inappropriate language.",
      (value) => !containsBadWords(value || "")
    )
    .matches(
      /^[a-zA-Z0-9 &()'-]*$/,
      "Business name must not contain special characters."
    )
    .required("Please enter the Business Name."),
  businessType: Yup.string().required("Please select the Business Type."),
  businessDescription: Yup.string().required(
    "Please enter the Business Description."
  ).test(
    "bad-word",
    "Business Descripton contains inappropriate language.",
    (value) => !containsBadWords(value || "")
  ),
  businessCountry: Yup.string().required(
    "Please select the Business Country."
  ),
  businessState: Yup.string().required(
    "Please select the Business State."
  ),
  website: Yup.string()
    .url("Invalid URL")
    .required("Please enter the website url."),
  taxId: Yup.string().required("please enter the tax id."),
  dateFormat: Yup.string().required("Please select the Date Format."),
  timeFormat: Yup.string().required("Please select the Time Format."),
});

const numberStringRegex = /^(?:0|[1-9]\d*)(?:\.\d+)?$/;

const costInfoValidationSchema = Yup.object({
  oneTimeSetupFee: Yup.string()
    .matches(
      numberStringRegex,
      "Please enter a valid number. Only numeric values greater than or equal to 0 are allowed."
    )
    .required("Please enter the One Time Setup Fee."),
  ongoingMaintenanceFee: Yup.string()
    .matches(
      numberStringRegex,
      "Please enter a valid number. Only numeric values greater than or equal to 0 are allowed."
    )
    .required("Please enter the Ongoing Maintenance Fee."),
  newMerchantSetupFee: Yup.string()
    .matches(
      numberStringRegex,
      "Please enter a valid number. Only numeric values greater than or equal to 0 are allowed."
    )
    .required("Please enter the New Merchant Setup Fee."),
  ongoingMaintenancePerMerchantFee: Yup.string()
    .matches(
      numberStringRegex,
      "Please enter a valid number. Only numeric values greater than or equal to 0 are allowed."
    )
    .required("Please enter the Ongoing Maintenance Per Merchant Fee."),
});

const kycValidationSchema = Yup.object({
  kycUserName: Yup.string()
    .trim()
    .test(
      "bad-word",
      "Username contains inappropriate language.",
      (value) => !containsBadWords(value || "")
    )
    .required("User name is required")
    .min(3, "User name must be at least 3 characters")
    .max(50, "User name must not exceed 50 characters"),

  kycCompanyName: yup
    .string()
    .trim()
    .required(VALIDATION.REQUIRED_FIELD("Kyc Company name"))
    .min(1, VALIDATION.MIN_LENGTH("Company name", 1))
    .max(160, VALIDATION.MAX_LENGTH("Company name", 160))
    .test(
      "bad-word",
      "Company Name contains inappropriate language.",
      (value) => !containsBadWords(value || "")
    )
    .matches(/^[a-zA-Z0-9_\-'&\s\u2018\u2019\u2018\u0060\u2013\u2014]+$/, VALIDATION.INVALID_FORMAT("Kyc Company name")),

  kycCompanyEmail: yup
    .string()
    .trim()
    .required(VALIDATION.REQUIRED_FIELD("Kyc Company email"))
    .email(VALIDATION.INVALID_EMAIL)
    .min(1, VALIDATION.MIN_LENGTH("Kyc Company email", 1))
    .max(70, VALIDATION.MAX_LENGTH("Kyc Company email", 70))
    .test(
      "bad-email",
      "Company Email contains inappropriate language.",
      (value) => !containsBadWordsInEmail(value || "")
    )
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, VALIDATION.INVALID_EMAIL),

  kycDomainUrl: Yup.string()
    .trim()
    .required("Domain URL is required")
    .test(
      "bad-word",
      "Domain Url contains inappropriate language.",
      (value) => !containsBadWords(value || "")
    )
    .matches(
      /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/,
      "Enter a valid domain (e.g. example.com)"
    ),

  kycPlanId: yup.mixed().required(VALIDATION.REQUIRED_FIELD("Plan")),

  kycBillingCycle: yup
    .string()
    .oneOf(["monthly", "yearly"], VALIDATION.INVALID_FORMAT("Billing cycle"))
    .required(VALIDATION.REQUIRED_FIELD("Billing cycle")),

  kycIsVerified: Yup.boolean().oneOf(
    [true],
    "Please verify before proceeding."
  ),

  kycIsAutoPay: Yup.boolean(),
  kycPaymentMode: Yup.string().required("Please select the Payment Mode"),
  kycCountry: Yup.string()
    .required("At least one country is required")
    .test(
      "valid-countries",
      "At least one country is required",
      (value) => {
        if (!value) return false;
        return value.split(",").filter(Boolean).length > 0;
      }
    ),
  kycCurrency: yup.string().trim().required(VALIDATION.SELECT_OPTION("Currency")),
});

const tripolaValidationSchema = yup.object({
  tripolaCompanyName: yup
    .string()
    .trim()
    .required(VALIDATION.REQUIRED_FIELD("Company name"))
    .min(1, VALIDATION.MIN_LENGTH("Company name", 1))
    .max(160, VALIDATION.MAX_LENGTH("Company name", 160))
    .test(
      "bad-word",
      "Company Name contains inappropriate language.",
      (value) => !containsBadWords(value || "")
    )
    .matches(
      /^[a-zA-Z0-9_\-'&\s\u2018\u2019\u2018\u0060\u2013\u2014]+$/,
      VALIDATION.INVALID_FORMAT("Company name")
    ),

  tripolaCompanyEmail: yup
    .string()
    .trim()
    .required(VALIDATION.REQUIRED_FIELD("Company email"))
    .email(VALIDATION.INVALID_EMAIL)
    .min(1, VALIDATION.MIN_LENGTH("Company email", 1))
    .max(70, VALIDATION.MAX_LENGTH("Company email", 70))
    .test(
      "bad-email",
      "Company Email contains inappropriate language.",
      (value) => !containsBadWordsInEmail(value || "")
    )
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      VALIDATION.INVALID_EMAIL
    ),

  tripolaCompanyZipCode: yup
    .string()
    .trim()
    .required(VALIDATION.REQUIRED_FIELD("Company zip code"))
    .min(4, VALIDATION.MIN_LENGTH("Company zip code", 4))
    .max(12, VALIDATION.MAX_LENGTH("Company zip code", 12))
    .matches(
      /^[a-zA-Z0-9\-\u2013\u2014]+$/,
      VALIDATION.INVALID_FORMAT("Company zip code")
    )
    .test(
      "not-all-zeros",
      VALIDATION.INVALID_FORMAT("Company zip code"),
      (value) => {
        if (!value) return true;
        return !/^0+$/.test(value.replace(/[\-\u2013\u2014]/g, ""));
      }
    ),
  tripolaCompanyCity: yup
    .string()
    .trim()
    .required(VALIDATION.REQUIRED_FIELD("Company City"))
    .min(1, VALIDATION.MIN_LENGTH("Company City", 1))
    .max(160, VALIDATION.MAX_LENGTH("Company City", 160))
    .test(
      "bad-word",
      "Company City contains inappropriate language.",
      (value) => !containsBadWords(value || "")
    )
    .matches(
      /^[a-zA-Z0-9_\-'&\s\u2018\u2019\u2018\u0060\u2013\u2014]+$/,
      VALIDATION.INVALID_FORMAT("Company City")
    ),

  tripolaCompanyPhoneNumber: yup
    .string()
    .required(VALIDATION.REQUIRED_FIELD("Company phone number"))
    .min(5, VALIDATION.MIN_LENGTH("Company phone number", 5))
    .max(15, VALIDATION.MAX_LENGTH("Company phone number", 15)),

  tripolaDomainUrl: yup
    .string()
    .required(VALIDATION.REQUIRED_FIELD("Domain URL"))
    .test(
      "bad-word",
      "Domain Url contains inappropriate language.",
      (value) => !containsBadWords(value || "")
    ),

  tripolaIsVerified: yup
    .boolean()
    .oneOf([true], VALIDATION.INVALID_FORMAT("Domain verification"))
    .required(VALIDATION.REQUIRED_FIELD("Domain verification")),

  tripolaCurrency: yup.string().trim().required(VALIDATION.SELECT_OPTION("Currency")),

  tripolaCompanyLogo: Yup.mixed()
    .required("Please select the company logo.")
    .test(
      "fileType",
      "Only JPEG, PNG and JPG files are allowed",
      (value) => {
        if (!value) {
          return true;
        }

        const isFile = value instanceof File;
        if (!isFile) {
          return true;
        }
        if (value) {
          const supportedFormats = ["jpeg", "png", "jpg"];
          return supportedFormats.includes(
            value.name.split(".").pop()
          );
        }
        return true;
      }
    )
    .test("fileSize", "File size should be less than 2 MB", (value) => {
      const maxFileSize = 2 * 1024 * 1024; // 2 MB
      if (!value) {
        return true;
      }

      const isFile = value instanceof File;
      if (!isFile) {
        return true;
      }

      return value.size <= maxFileSize;
    }),
});

const tripolaValidationPlanStructureSchema = yup.object({
  tripolaPlanId: yup.mixed().required(VALIDATION.REQUIRED_FIELD("Plan")),
  tripolaBillingCycle: yup
    .string()
    .oneOf(["monthly", "yearly"], VALIDATION.INVALID_FORMAT("Billing cycle"))
    .required(VALIDATION.REQUIRED_FIELD("Billing cycle")),
  tripolaIsAutoPay: yup.string()
});

const AddEditTenant = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tenantId = location.state?.id;
  const isCloneTenant = location.state?.isCloneTenant || false;
  // const { id = null } = location.state || {};
  const { id = null, source = "common", assignPortal = null } = location.state || {};
  const [tenantData, setTenantData] = useState(null);
  const [originalQbotData, setOriginalQbotData] = useState({});
  const [originalPaycentralData, setOriginalPaycentralData] = useState({});
  const [originalKycData, setOriginalKycData] = useState({});
  const [originalTripolaData, setOriginalTripolaData] = useState({});
  const userData = localStorage.getItem("userData") ? getUserData() : null;
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(Boolean(id));
  const refDataLoaded = useRef({
    core: false,
    paycentral: false,
    kyc: false,
    tripola: false,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [countryData, setCountryData] = useState([]);
  const [stateData, setStateData] = useState([]);
  const [tenantCurrencies, setTenantCurrencies] = useState([]);
  const [planMapPrice, setPlanMapPrice] = useState({});
  const [merchantDetails, setMerchantDetails] = useState({});
  const [logoRemoved, setLogoRemoved] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [isDomainEditing, setIsDomainEditing] = useState(false);
  const [isQbotEdit, setIsQbotEdit] = useState(false);
  const [isPaycentralEdit, setIsPaycentralEdit] = useState(false);
  const [isTripolaEdit, setIsTripolaEdit] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [buttonColor, setButtonColor] = useState("");
  const [timeZone, setTimeZone] = useState([]);
  const [businessType, setBusinessType] = useState([]);
  const [businessStateData, setBusinessStateData] = useState([]);
  const organizationDataById = location.state?.organizationDataById;

  const [kycCountryList, setKycCountryList] = useState([]);
  const [kycPlanList, setKycPlanList] = useState([]);
  const [tripolaPlanList, setTripolaPlanList] = useState([]);

  const isEditMode = Boolean(id) && !isCloneTenant;

  const safeJsonParse = (value) => {
    try {
      if (typeof value === "string") {
        return JSON.parse(value);
      }
      return value || {}; // already object or null
    } catch (e) {
      console.error("JSON parse error:", value);
      return {};
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // console.log("TENANT DATA", tenantData);

  const commonFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: tenantData?.firstName || "",
      lastName: tenantData?.lastName || "",
      email: isCloneTenant ? "" : (tenantData?.email || ""),
      companyPhoneNumber: tenantData?.phoneNumber || "",
      companyCountry: tenantData?.country || "",
      companyState: tenantData?.state || "",
      companyAddress: tenantData?.address || "",

      isQbot: tenantData?.assignPortal?.isQbot ?? false,
      isPaycentral: tenantData?.assignPortal?.isPaycentral ?? false,
      isKyc: tenantData?.assignPortal?.isKyc ?? false,
      isTripola: tenantData?.assignPortal?.isTripola ?? false,

      hasTrialConfiguration: tenantData?.hasTrialConfiguration ?? false,
      isTrialConfig: tenantData?.hasTrialConfiguration ?? false,
      trialPortal: Array.isArray(tenantData?.trialPortal) ? tenantData?.trialPortal : (tenantData?.trialPortal ? [tenantData?.trialPortal] : []),
      trialDays: tenantData?.trialDays ?? {},
      graceDays: tenantData?.graceDays ?? null,

      slaId: tenantData?.slaId || "",
    },
    validateOnChange: true,
    validateOnBlur: true,
    validationSchema: Yup.object({
      firstName: Yup.string().required("Please enter first name")
        .test(
          "bad-word",
          "First Name contains inappropriate language.",
          (value) => !containsBadWords(value || "")
        ),
      lastName: Yup.string().required("Please enter last name")
        .test(
          "bad-word",
          "Last Name contains inappropriate language.",
          (value) => !containsBadWords(value || "")
        ),
      email: Yup.string().email("Please enter a valid email address")
        .matches(/^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/, "Email must be in lowercase")
        .test(
          "bad-email",
          "Email contains inappropriate language.",
          (value) => !containsBadWordsInEmail(value || "")
        ).required("Please enter email address")
      ,
      companyPhoneNumber: Yup.string().required("Please enter company phone number")
        .test(
          "bad-word",
          "Phone Number contains inappropriate language.",
          (value) => !containsBadWords(value || "")
        ),
      companyCountry: Yup.string().required("Please select country"),
      companyState: Yup.string().required("Please select state"),
      companyAddress: Yup.string().required("Please enter company address")
        .test(
          "bad-word",
          "Address   contains inappropriate language.",
          (value) => !containsBadWords(value || "")
        ),
      isQbot: Yup.boolean(),
      isPaycentral: Yup.boolean(),
      isKyc: Yup.boolean(),
      isTripola: Yup.boolean(),
      isTrialConfig: Yup.boolean(),
      atLeastOneProduct: Yup.boolean().oneOf(
        [true],
        "Please select at least one product"
      ),
      trialPortal: Yup.array().of(Yup.string()).when("isTrialConfig", {
        is: true,
        then: (schema) => schema.min(1, "Please select at least one trial portal").required("Please select a trial portal"),
        otherwise: (schema) => schema.notRequired(),
      }),
      trialDays: Yup.mixed().when("isTrialConfig", {
        is: true,
        then: (schema) =>
          Yup.object().test("all-portals-have-days", "Please enter trial days for all selected portals", function (value) {
            const { trialPortal } = this.parent;
            if (!trialPortal || trialPortal.length === 0) return true;

            // Check if value is an object with all portal keys
            if (typeof value !== "object" || value === null) return false;

            // Ensure every selected portal has a trial days value >= 7
            return trialPortal.every((portal) => {
              const days = value[portal];
              return typeof days === "number" && days >= 7;
            });
          }),
        otherwise: (schema) => schema.notRequired(),
      }),
      // graceDays: Yup.number()
      //   .typeError("Grace days must be a number")
      //   .integer("Grace days must be a whole number")
      //   .min(0, "Grace days cannot be negative")
      //   .when("isTrialConfig", {
      //     is: true,
      //     then: (schema) => schema.required("Please enter grace days"),
      //     otherwise: (schema) => schema.notRequired(),
      //   }),
      graceDays: Yup.number()
        .transform((value, originalValue) =>
          originalValue === "" ? undefined : value
        )
        .typeError("Grace days must be a number")
        .integer("Grace days must be a whole number")
        .min(0, "Grace days cannot be negative")
        .when(
          ["isTrialConfig", "hasTrialConfiguration"],
          ([isTrialConfig, hasTrialConfiguration], schema) =>
            isTrialConfig && hasTrialConfiguration
              ? schema.required("Please enter grace days")
              : schema.notRequired()
        ),
      slaId: Yup.number().nullable().notRequired(),
    }),
  });

  // const visiblePortals = {
  //   isQbot:
  //     source === "common"
  //       ? commonFormik?.values?.isQbot
  //       : source === "qbot",

  //   isPaycentral:
  //     source === "common"
  //       ? commonFormik?.values?.isPaycentral
  //       : source === "paycentral",

  //   isKyc:
  //     source === "common"
  //       ? commonFormik?.values?.isKyc
  //       : source === "kyc",

  //   isTripola:
  //     source === "common"
  //       ? commonFormik?.values?.isTripola
  //       : source === "tripola",
  // };

  const visiblePortals = {
    isQbot:
      commonFormik.values.isQbot ||
      commonFormik.values.trialPortal?.includes("qbot"),

    isPaycentral:
      commonFormik.values.isPaycentral ||
      commonFormik.values.trialPortal?.includes("paycentral"),

    isKyc:
      commonFormik.values.isKyc ||
      commonFormik.values.trialPortal?.includes("kyc"),

    isTripola:
      commonFormik.values.isTripola ||
      commonFormik.values.trialPortal?.includes("tripola"),
  };

  useEffect(() => {
    if (!commonFormik.values) return;

    tenantInformationFormik.setValues((prev) => ({
      ...prev,
      firstName: commonFormik.values.firstName,
      lastName: commonFormik.values.lastName,
      email: commonFormik.values.email,
      companyAddress: commonFormik.values.companyAddress,
      companyCountry: commonFormik.values.companyCountry,
      companyState: commonFormik.values.companyState,
    }));
  }, [
    commonFormik.values.firstName,
    commonFormik.values.lastName,
    commonFormik.values.email,
    commonFormik.values.companyAddress,
    commonFormik.values.companyCountry,
    commonFormik.values.companyState,
  ]);

  useEffect(() => {
    if (!commonFormik.values) return;

    organizationInfoFormik.setValues((prev) => ({
      ...prev,
      firstName: commonFormik.values.firstName,
      lastName: commonFormik.values.lastName,
      email: commonFormik.values.email,
      phoneNumber: commonFormik.values.companyPhoneNumber,
      address: commonFormik.values.companyAddress,
      businessCountry: commonFormik.values.companyCountry,
      businessState: commonFormik.values.companyState,
      country: commonFormik.values.companyCountry,
      state: commonFormik.values.companyState,
    }));
  }, [
    commonFormik.values.firstName,
    commonFormik.values.lastName,
    commonFormik.values.email,
    commonFormik.values.companyPhoneNumber,
    commonFormik.values.companyAddress,
    commonFormik.values.companyCountry,
    commonFormik.values.companyState,
  ]);

  useEffect(() => {
    if (!commonFormik.values) return;

    kycFormik.setValues((prev) => ({
      ...prev,
      firstName: commonFormik.values.firstName,
      lastName: commonFormik.values.lastName,
      email: commonFormik.values.email,
      kycCompanyPhoneNumber: commonFormik.values.companyPhoneNumber,
    }));
  }, [
    commonFormik.values.firstName,
    commonFormik.values.lastName,
    commonFormik.values.email,
    commonFormik.values.companyPhoneNumber
  ]);

  useEffect(() => {
    if (!commonFormik.values) return;

    tripolaFormik.setValues((prev) => ({
      ...prev,
      firstName: commonFormik.values.firstName,
      lastName: commonFormik.values.lastName,
      email: commonFormik.values.email,
      companyAddress: commonFormik.values.companyAddress,
      companyCountry: commonFormik.values.companyCountry,
      companyState: commonFormik.values.companyState,
    }));
  }, [
    commonFormik.values.firstName,
    commonFormik.values.lastName,
    commonFormik.values.email,
    commonFormik.values.companyAddress,
    commonFormik.values.companyCountry,
    commonFormik.values.companyState,
  ]);

  // ================= EXISTING FORMIKS =================
  const tenantInformationFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      companyName: "",
      currency: "",
      companyEmail: "",
      companyZipCode: "",
      domainUrl: "",
      isVerified: false,
      whitelistIps: [],
      isWhiteListIp: false,
      brandingName: "",
      brandingLogo: "",
    },
    validationSchema: validationMerchantInformationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      // console.log("values", values);
    },
  });

  const merchantPlanStructureFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      planId: null,
      paymentMode: "online",
      billingCycle: null,
      isAutoPay: true,
      isCustomPlan: true,
      customPlanDetails: {},
      allowedFeatures: {},
      hideBillingDetails: false,
    },
    validationSchema: validationPlanStructureSchema,
    onSubmit: (values) => {
      // console.log("values", values);
    },
  });

  // useEffect(() => {
  //   const fetchMerchantDetails = async () => {
  //     try {
  //       const { status, data } = await getTenantDetailByIdAction(id);
  //       if (status === 200) {
  //         setMerchantDetails(data);
  //         tenantInformationFormik.setValues({
  //           ...data,
  //           companyName: isCloneTenant ? "" : (data.companyName || ""),
  //           companyEmail: isCloneTenant ? "" : (data.companyEmail || ""),
  //           domainUrl: isCloneTenant ? "" : (data.domainUrl || ""),
  //           isVerified: isCloneTenant ? false : true,
  //           isWhiteListIp: false,
  //           whitelistIps: [],
  //         });
  //         merchantPlanStructureFormik.setValues({
  //           ...data,
  //         });
  //       }
  //     } catch (error) {
  //       setSnackbar({
  //         open: true,
  //         message: error.response.data.message,
  //         severity: "error",
  //       });
  //     }
  //   };

  //   if (id) fetchMerchantDetails();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [id]);

  // Core reference data only — portal-specific lists load on demand
  useEffect(() => {
    if (refDataLoaded.current.core) return;
    refDataLoaded.current.core = true;
    Promise.all([getMerchantCurrenciesData(), getCountries()]).catch(() => {});
  }, []);

  const organizationInfoFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      organizationLogo: organizationDataById
        ? organizationDataById?.organizationLogo
        : null,
      // imagePreview: organizationDataById
      //   ? `${PAYCENTRAL_MEDIA_URL}/${organizationDataById.organizationLogo}`
      //   : "",
      imagePreview: organizationDataById
        ? `${PAYCENTRAL_MEDIA_URL}/${organizationDataById.organizationLogo}`
        : "",
      userName: organizationDataById
        ? organizationDataById?.userName
        : "",
      isVerified: organizationDataById ? true : false,
      businessName: organizationDataById
        ? organizationDataById?.businessName
        : "",
      businessType: organizationDataById
        ? organizationDataById?.businessType
        : "",
      businessDescription: organizationDataById
        ? organizationDataById?.businessDescription
        : "",
      businessCountry: organizationDataById
        ? organizationDataById?.businessCountry
        : "",
      businessState: organizationDataById
        ? organizationDataById?.businessState
        : "",
      domainUrl: organizationDataById
        ? organizationDataById?.domainUrl
        : "",
      organizationName: organizationDataById
        ? organizationDataById?.organizationName
        : "",
      website: organizationDataById
        ? organizationDataById?.websiteURL
        : "",
      taxId: organizationDataById ? organizationDataById?.taxId : "",
      dateFormat: organizationDataById
        ? organizationDataById?.dateFormat
        : "",
      timeZone: organizationDataById
        ? organizationDataById?.timeZone
        : "",
      timeFormat: organizationDataById
        ? organizationDataById?.timeFormat
        : "",
      // ipAddress: organizationDataById
      //   ? organizationDataById?.ipAddress
      //   : "",
      isAutoInvoice: organizationDataById?.isAutoInvoice ?? false,
      isActive: organizationDataById?.isActive ?? true,
    },
    validationSchema: organizationValidationSchema,
    onSubmit: (values) => {
      // console.log("values", values);
    },
  });

  const kycFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      kycUserName: "",
      kycCompanyName: "",
      kycCompanyEmail: "",
      kycDomainUrl: "",
      kycCountry: "",
      kycIsVerified: false,
      kycPlanId: null,
      kycBillingCycle: null,
      kycIsAutoPay: true,
      kycPaymentMode: "",
      kycCurrency: "",
    },
    validationSchema: kycValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      // console.log("KYC Values:", values);
    },
  });

  const tripolaFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      tripolaCompanyName: "",
      tripolaCompanyEmail: "",
      tripolaCompanyZipCode: "",
      tripolaCompanyCity: "",
      tripolaCompanyPhoneNumber: "",
      tripolaDomainUrl: "",
      tripolaIsVerified: false,
      tripolaCurrency: "",
      tripolaCompanyLogo: "",
    },
    validationSchema: tripolaValidationSchema,
    onSubmit: (values) => {
      // console.log("values", values);
    },
  });

  const tripolaPlanStructureFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      tripolaPlanId: null,
      tripolaBillingCycle: null,
      tripolaIsAutoPay: true,
    },
    validationSchema: tripolaValidationPlanStructureSchema,
    onSubmit: (values) => {
      // console.log("values", values);
    },
  });

  const parsedCostInfo = safeJsonParse(organizationDataById?.costInfo);

  const costInfoFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      oneTimeSetupFee: parsedCostInfo.oneTimeSetupFee || "",
      ongoingMaintenanceFee: parsedCostInfo.ongoingMaintenanceFee || "",
      newMerchantSetupFee: parsedCostInfo.newMerchantSetupFee || "",
      ongoingMaintenancePerMerchantFee:
        parsedCostInfo.ongoingMaintenancePerMerchantFee || "",
    },
    validationSchema: costInfoValidationSchema,
    onSubmit: (values) => {
      // console.log("values", values);
    },
  });

  // ================= EDIT MODE SUPPORT =================

  const getCountries = async () => {
    try {
      const res = await getCountriesDataAction();
      const { data } = res?.data;
      setCountryData(data);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to fetch countries",
        severity: "error",
      });
    }
  };

  const getStateData = async (countryName) => {
    if (!countryName) {
      setStateData([]);
      return;
    }
    try {
      const res = await getStateAction({ countryName });
      const { data } = res?.data;
      setStateData(data);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || "Failed to fetch states",
        severity: "error",
      });
    }
  };

  const getBusinessStateData = async (countryName) => {
    if (!countryName) {
      setBusinessStateData([]);
      return;
    }

    try {
      const res = await getStateAction({ countryName });
      const { data } = res?.data;
      setBusinessStateData(data); // ← update business states only
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || "Failed to fetch business states",
        severity: "error",
      });
    }
  };

  const getBusinessTypeData = async () => {
    await getBusinessType()
      .then((res) => {
        const { data } = res.data;
        setBusinessType(data);
      })
      .catch((error) => {
        setSnackbar({
          open: true,
          message: error.message,
          severity: "error",
        });
      });
  };

  const getTimeZonesData = async () => {
    try {
      const res = await getTimeZone();
      setTimeZone(res?.data || []);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to fetch timezones",
        severity: "error",
      });
    }
  };

  const getMerchantCurrenciesData = async () => {
    try {
      const res = await getTenantCurrenciesAction();
      setTenantCurrencies(res?.data?.data || []);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Failed to fetch currencies",
        severity: "error",
      });
    }
  };

  const fetchKycCountries = async () => {
    try {
      const response = await listCountriesAction();
      setKycCountryList(response?.data?.data?.rows || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchKycPlans = async () => {
    try {
      const response = await listAllKycPlansDropDownAction();
      setKycPlanList(response?.data?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchTripolaPlans = async () => {
    try {
      const response = await listTripolaPlansForDropdownAction();
      setTripolaPlanList(response?.data?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  // Paycentral lookups only when that portal is used (or edit/clone)
  useEffect(() => {
    const needsPaycentral =
      Boolean(id) ||
      commonFormik.values.isPaycentral ||
      commonFormik.values.trialPortal?.includes("paycentral");
    if (!needsPaycentral || refDataLoaded.current.paycentral) return;
    refDataLoaded.current.paycentral = true;
    Promise.all([getBusinessTypeData(), getTimeZonesData()]).catch(() => {});
  }, [
    id,
    commonFormik.values.isPaycentral,
    commonFormik.values.trialPortal,
  ]);

  // KYC lookups only when that portal is used (or edit/clone)
  useEffect(() => {
    const needsKyc =
      Boolean(id) ||
      commonFormik.values.isKyc ||
      commonFormik.values.trialPortal?.includes("kyc");
    if (!needsKyc || refDataLoaded.current.kyc) return;
    refDataLoaded.current.kyc = true;
    Promise.all([fetchKycCountries(), fetchKycPlans()]).catch(() => {});
  }, [id, commonFormik.values.isKyc, commonFormik.values.trialPortal]);

  // Tripola plans only when that portal is used (or edit/clone)
  useEffect(() => {
    const needsTripola =
      Boolean(id) ||
      commonFormik.values.isTripola ||
      commonFormik.values.trialPortal?.includes("tripola");
    if (!needsTripola || refDataLoaded.current.tripola) return;
    refDataLoaded.current.tripola = true;
    fetchTripolaPlans().catch(() => {});
  }, [id, commonFormik.values.isTripola, commonFormik.values.trialPortal]);

  const fetchUserProducts = async (tenantId) => {
    try {
      const response = await getAllTenantDetailByIdAction(tenantId);
      // console.log("respomseeeeeeeedsata",response)
      const payload = response?.data?.data || {};
      const { common, qbotTenant, paycentralTenant, kycTenant, tripolaTenant } = payload;

      if (common) {
        setTenantData(common);
      }

      // COMMON TENANT
      if (common) {
        commonFormik.setValues((prev) => ({
          ...prev,
          firstName: common.firstName || "",
          lastName: common.lastName || "",
          email: isCloneTenant ? "" : (common.email || ""),
          companyPhoneNumber: common.phoneNumber || "",
          companyCountry: common.country || "",
          companyState: common.state || "",
          companyAddress: common.address || "",
          hasTrialConfiguration: common.hasTrialConfiguration ?? false,
          isTrialConfig: common.hasTrialConfiguration ?? false,
          trialPortal: common.trialPortal || [],
          trialDays: common.trialDays ?? null,
          graceDays: common.graceDays ?? null,
          isQbot: common.assignPortal?.isQbot ?? prev.isQbot,
          isPaycentral: common.assignPortal?.isPaycentral ?? prev.isPaycentral,
          isKyc: common.assignPortal?.isKyc ?? prev.isKyc,
          isTripola: common.assignPortal?.isTripola ?? prev.isTripola,
        }));
      }

      // QBOT
      if (qbotTenant) {
        setOriginalQbotData(qbotTenant);
        tenantInformationFormik.setValues(prev => ({
          ...prev,
          ...qbotTenant,
          companyName: isCloneTenant ? "" : (qbotTenant.companyName || ""),
          currency: qbotTenant.currency || "",
          companyEmail: isCloneTenant ? "" : (qbotTenant.companyEmail || ""),
          companyZipCode: qbotTenant.companyZipCode || "",
          domainUrl: isCloneTenant ? "" : (qbotTenant.domainUrl || ""),
          // whitelistIps: qbotTenant.whitelistIps || [],
          isWhiteListIp: qbotTenant.ipRestriction || false,
          whitelistIps:
            typeof qbotTenant.whitelistIps === "string"
              ? JSON.parse(qbotTenant.whitelistIps || "[]")
              : qbotTenant.whitelistIps || [],
          companyAddress: qbotTenant.companyAddress || "",
          companyPhoneNumber: qbotTenant.companyPhoneNumber || "",
          isVerified: isCloneTenant ? false : true
        }));

        merchantPlanStructureFormik.setValues({
          planId: qbotTenant.planId || "",
          billingCycle: qbotTenant.planHistory?.[0]?.billingCycle || "",
          paymentMode: qbotTenant?.paymentMode || "",
          isAutoPay: qbotTenant.isAutoPay || false,
          allowedFeatures:
            typeof qbotTenant.allowedFeatures === "string"
              ? JSON.parse(qbotTenant.allowedFeatures || "{}")
              : qbotTenant.allowedFeatures || {}
        });
      }

      // PAYCENTRAL
      if (paycentralTenant) {
        setOriginalPaycentralData(paycentralTenant);
        organizationInfoFormik.setValues({
          userName: isCloneTenant ? "" : (paycentralTenant.userName || ""),
          firstName: paycentralTenant.firstName || "",
          lastName: paycentralTenant.lastName || "",
          domainUrl: isCloneTenant ? "" : (paycentralTenant.domainUrl || ""),
          organizationName: paycentralTenant.organizationName || "",
          email: isCloneTenant ? "" : (paycentralTenant.email || ""),
          phoneNumber: paycentralTenant.phoneNumber || "",
          address: paycentralTenant.address || "",
          website: paycentralTenant.websiteURL || "",
          businessName: paycentralTenant.businessName || "",
          businessType: paycentralTenant.businessType || "",
          businessDescription: paycentralTenant.businessDescription || "",
          businessCountry: paycentralTenant.businessCountry || "",
          businessState: paycentralTenant.businessState || "",
          taxId: paycentralTenant.taxId || "",
          dateFormat: paycentralTenant.dateFormat || "",
          timeFormat: paycentralTenant.timeFormat || "",
          timeZone: paycentralTenant.timeZone || "",
          organizationLogo: paycentralTenant.organizationLogo || null,
          imagePreview: paycentralTenant.organizationLogo
            ? `${PAYCENTRAL_MEDIA_URL}/${paycentralTenant.organizationLogo}`
            : "",
          isVerified: isCloneTenant ? false : true,
          isActive: paycentralTenant.isActive ?? true,
          isAutoInvoice: paycentralTenant.isAutoInvoice ?? false,
        });

        if (paycentralTenant.costInfo) {
          const parsedCost = safeJsonParse(paycentralTenant.costInfo) || {};

          costInfoFormik.setValues({
            oneTimeSetupFee: parsedCost.oneTimeSetupFee || "",
            ongoingMaintenanceFee: parsedCost.ongoingMaintenanceFee || "",
            newMerchantSetupFee: parsedCost.newMerchantSetupFee || "",
            ongoingMaintenancePerMerchantFee:
              parsedCost.ongoingMaintenancePerMerchantFee || ""
          });
        }
      }

      // KYC
      if (kycTenant) {
        setOriginalKycData(kycTenant);
        kycFormik.setValues((prev) => ({
          ...prev,
          kycUserName: isCloneTenant ? "" : (kycTenant.userName || ""),
          kycCompanyName: isCloneTenant ? "" : (kycTenant.companyName || ""),
          kycCompanyEmail: isCloneTenant ? "" : (kycTenant.companyEmail || ""),
          kycDomainUrl: isCloneTenant ? "" : (kycTenant.domainUrl || ""),
          kycCountry: kycTenant.country || "",
          kycCompanyPhoneNumber: kycTenant.kycCompanyPhoneNumber || "",
          kycPlanId: kycTenant.planId || null,
          kycBillingCycle: kycTenant.BillingDetails || null,
          kycIsAutoPay: kycTenant.isAutoPay ?? true,
          kycPaymentMode: kycTenant.kycPaymentMode || kycTenant.paymentMode || "offline",
          kycIsVerified: isCloneTenant ? false : true,
          kycCurrency: kycTenant.currency || "",
        }));
      }

      // TRIPOLA
      if (tripolaTenant) {
        setOriginalTripolaData(tripolaTenant);
        tripolaFormik.setValues(prev => ({
          ...prev,
          ...tripolaTenant,
          tripolaCompanyName: isCloneTenant ? "" : (tripolaTenant.companyName || ""),
          tripolaCompanyEmail: isCloneTenant ? "" : (tripolaTenant.companyEmail || ""),
          tripolaCompanyZipCode: tripolaTenant.companyZipCode || "",
          tripolaCompanyCity: tripolaTenant.companyCity || "",
          tripolaCompanyPhoneNumber: tripolaTenant.companyPhoneNumber || "",
          tripolaDomainUrl: isCloneTenant ? "" : (tripolaTenant.domainUrl || ""),
          tripolaIsVerified: isCloneTenant ? false : true,
          tripolaCurrency: tripolaTenant.currency || "",
        }));

        tripolaPlanStructureFormik.setValues({
          tripolaPlanId: tripolaTenant.planId || "",
          tripolaBillingCycle: tripolaTenant.billingCycle || "",
          tripolaIsAutoPay: tripolaTenant.isAutoPay || false
        });
      }

    } catch (error) {
      console.error("Error fetching tenant:", error);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (!id) {
        if (!cancelled) setPageLoading(false);
        return;
      }

      if (!cancelled) setPageLoading(true);
      try {
        setIsEditing(false);
        // Single fetch — previously called getAllTenantDetailByIdAction twice
        await fetchUserProducts(id);
      } catch (error) {
        console.error("Error fetching tenant details:", error);

        if (!cancelled) {
          setSnackbar({
            open: true,
            message: error?.response?.data?.message || "Something went wrong.",
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, source]);

  useEffect(() => {
    const country =
      tenantInformationFormik?.values?.companyCountry ||
      commonFormik?.values?.companyCountry;

    getStateData(country);
  }, [
    commonFormik?.values?.companyCountry,
    tenantInformationFormik?.values?.companyCountry
  ]);

  const dateFormatOptions = [
    { label: "M/d/Y (Mar/05/1999)", value: "M/d/Y" },
    { label: "d/m/Y (05/03/1999)", value: "d/m/Y" },
    { label: "m/d/Y (03/05/1999)", value: "m/d/Y" },
    { label: "Y/m/d (1999/03/05)", value: "Y/m/d" },
    { label: "Y/d/m (1999/05/03)", value: "Y/d/m" },
    { label: "M d Y (Mar 05 1999)", value: "M d Y" },
  ];

  const timeFormatOptions = [
    { label: "12-hour format (2:30 PM)", value: "h:mm a" },
    { label: "24-hour format (14:30)", value: "HH:mm" },
  ];

  const handleDateFormatChange = (event, newValue) => {
    if (newValue) {
      organizationInfoFormik.setFieldValue("dateFormat", newValue.value);
    }
  };

  const handleTimeZoneChange = (event, newValue) => {
    if (newValue) {
      organizationInfoFormik.setFieldValue("timeZone", newValue.timeZone);
    }
  };

  const handleTimeFormatChange = (event, newValue) => {
    if (newValue) {
      organizationInfoFormik.setFieldValue("timeFormat", newValue.value);
    }
  };

  useEffect(() => {
    if (!commonFormik.values.isQbot && !commonFormik.values.trialPortal?.includes("qbot") && !id) {
      tenantInformationFormik.resetForm();
      merchantPlanStructureFormik.resetForm();
    }
  }, [commonFormik.values.isQbot, id, commonFormik.values.trialPortal]);

  useEffect(() => {
    if (
      !commonFormik.values.isPaycentral &&
      !commonFormik.values.trialPortal?.includes("paycentral") &&
      !id
    ) {
      organizationInfoFormik.resetForm();
      costInfoFormik.resetForm();
    }
  }, [commonFormik.values.isPaycentral, commonFormik.values.trialPortal, id]);


  useEffect(() => {
    if (
      !commonFormik.values.isKyc &&
      !commonFormik.values.trialPortal?.includes("kyc") &&
      !id
    ) {
      kycFormik.resetForm();
    }
  }, [commonFormik.values.isKyc, commonFormik.values.trialPortal, id]);

  useEffect(() => {
    if (
      !commonFormik.values.isTripola &&
      !commonFormik.values.trialPortal?.includes("tripola") &&
      !id
    ) {
      tripolaFormik.resetForm();
    }
  }, [commonFormik.values.isTripola, commonFormik.values.trialPortal, id]);


  useEffect(() => {
    const clean = (v) =>
      v?.trim().toLowerCase().replace(/^https?:\/\//, "");

    const qbot = clean(tenantInformationFormik.values.domainUrl);
    const pay = clean(organizationInfoFormik.values.domainUrl);
    const kyc = clean(kycFormik.values.kycDomainUrl);
    const tripola = clean(tripolaFormik.values.tripolaDomainUrl);

    // clear old errors first
    tenantInformationFormik.setFieldError("domainUrl", "");
    organizationInfoFormik.setFieldError("domainUrl", "");
    kycFormik.setFieldError("kycDomainUrl", "");
    tripolaFormik.setFieldError("tripolaDomainUrl", "");

    if (qbot && pay && qbot === pay) {
      tenantInformationFormik.setFieldError(
        "domainUrl",
        "QBOT and PayCentral domains cannot be the same"
      );

      organizationInfoFormik.setFieldError(
        "domainUrl",
        "QBOT and PayCentral domains cannot be the same"
      );
    }

    if (qbot && kyc && qbot === kyc) {
      tenantInformationFormik.setFieldError(
        "domainUrl",
        "QBOT and KYC domains cannot be the same"
      );

      kycFormik.setFieldError(
        "kycDomainUrl",
        "QBOT and KYC domains cannot be the same"
      );
    }

    if (pay && kyc && pay === kyc) {
      organizationInfoFormik.setFieldError(
        "domainUrl",
        "PayCentral and KYC domains cannot be the same"
      );

      kycFormik.setFieldError(
        "kycDomainUrl",
        "PayCentral and KYC domains cannot be the same"
      );
    }

    if (tripola && qbot && tripola === qbot) {
      tripolaFormik.setFieldError(
        "tripolaDomainUrl",
        "Tripola and QBOT domains cannot be the same"
      );
    }
    if (tripola && pay && tripola === pay) {
      tripolaFormik.setFieldError(
        "tripolaDomainUrl",
        "Tripola and PayCentral domains cannot be the same"
      );
    }
    if (tripola && kyc && tripola === kyc) {
      tripolaFormik.setFieldError(
        "tripolaDomainUrl",
        "Tripola and KYC domains cannot be the same"
      );
    }

  }, [
    tenantInformationFormik.values.domainUrl,
    organizationInfoFormik.values.domainUrl,
    kycFormik.values.kycDomainUrl,
    tripolaFormik.values.tripolaDomainUrl
  ]);

  // ================= DYNAMIC STEPS =================
  const getSteps = () => {
    // const isQbot = isEditMode
    //   ? visiblePortals.isQbot
    //   : commonFormik?.values?.isQbot;

    // const isPaycentral = isEditMode
    //   ? visiblePortals.isPaycentral
    //   : commonFormik?.values?.isPaycentral;

    // const isKyc = isEditMode
    //   ? visiblePortals.isKyc
    //   : commonFormik?.values?.isKyc;

    // const isTripola = isEditMode
    //   ? visiblePortals.isTripola
    //   : commonFormik?.values?.isTripola;
    const isQbot = visiblePortals.isQbot;
    const isPaycentral = visiblePortals.isPaycentral;
    const isKyc = visiblePortals.isKyc;
    const isTripola = visiblePortals.isTripola;

    let steps = ["Basic Info"];

    if (isQbot) {
      steps.push("Qbot Information");
      steps.push("Qbot Plan Structure");
    }

    if (isPaycentral) {
      steps.push("Corporation Information");
      steps.push("Corporation Plan Structure");
    }

    if (isKyc) {
      steps.push("KYC Information");
    }

    if (isTripola) {
      steps.push("Tripola Information");
      steps.push("Tripola Plan Structure");
    }

    // steps.push("Preview");
    if (isQbot) {
      steps.push("Qbot Preview");
    }

    if (isPaycentral) {
      steps.push("Corporation Preview");
    }

    if (isKyc) {
      steps.push("KYC Preview");
    }

    if (isTripola) {
      steps.push("Tripola Preview");
    }

    return steps;
  };

  const steps = getSteps();

  const stepDescriptions = {
    "Basic Info": "Tell us about yourself to get started.",
    "Qbot Information": "Configure your QBOT details.",
    "Qbot Plan Structure": "Choose your QBOT plan.",
    "Corporation Information": "Set up corporation details.",
    "Corporation Plan Structure": "Set up corporation cost details.",
    "KYC Information": "Set up your KYC details.",
    "Tripola Information": "Set up Tripola details.",
    "Tripola Plan Structure": "Choose Tripola plan.",
    "Qbot Preview": "Review QBOT information.",
    "Corporation Preview": "Review corporation information.",
    "KYC Preview": "Review KYC information.",
    "Tripola Preview": "Review Tripola information.",
  };

  const isTrialPortalCreation =
    isEditMode &&
    commonFormik.values.isTrialConfig;

  const isQbotTrial =
    isTrialPortalCreation &&
    commonFormik.values.trialPortal?.includes("qbot");

  const isPaycentralTrial =
    isTrialPortalCreation &&
    commonFormik.values.trialPortal?.includes("paycentral");

  const isKycTrial =
    isTrialPortalCreation &&
    commonFormik.values.trialPortal?.includes("kyc");

  const isTripolaTrial =
    isTrialPortalCreation &&
    commonFormik.values.trialPortal?.includes("tripola");

  // ================= STEP CONTENT =================
  const getStepContent = (step) => {
    // const isQbot = isEditMode
    //   ? visiblePortals.isQbot
    //   : commonFormik?.values?.isQbot;

    // const isPaycentral = isEditMode
    //   ? visiblePortals.isPaycentral
    //   : commonFormik?.values?.isPaycentral;

    // const isKyc = isEditMode
    //   ? visiblePortals.isKyc
    //   : commonFormik?.values?.isKyc;

    // const isTripola = isEditMode
    //   ? visiblePortals.isTripola
    //   : commonFormik?.values?.isTripola;
    const isQbot = visiblePortals.isQbot;
    const isPaycentral = visiblePortals.isPaycentral;
    const isKyc = visiblePortals.isKyc;
    const isTripola = visiblePortals.isTripola;

    let index = 0;

    // STEP 1
    if (step === index) return <CommonStep commonFormik={commonFormik}
      countryData={countryData}
      stateData={stateData}
      getStateData={getStateData}
      isEditing={isEditing}
      isEditMode={isEditMode}
    />;
    index++;

    // QBOT FLOW
    if (isQbot) {
      if (step === index)
        return (
          <TenantInformationStep
            tenantId={isCloneTenant ? null : id}
            formik={tenantInformationFormik}
            handleEditing={handleEditing}
            isEditing={isEditing}
            isDomainEditing={isDomainEditing}
            tenantCurrencies={tenantCurrencies}
            countryData={countryData}
            stateData={stateData}
            merchantDetails={merchantDetails}
            logoRemoved={logoRemoved}
            setLogoRemoved={setLogoRemoved}
            isQbotEdit={isQbotEdit}
            setIsQbotEdit={setIsQbotEdit}
            isTrialPortalCreation={isQbotTrial}
            setIsDomainEditing={setIsDomainEditing}
          />
        );
      index++;

      if (step === index)
        return (
          <TenantPlanStructureStep
            tenantId={isCloneTenant ? null : id}
            formik={merchantPlanStructureFormik}
            planMapPrice={planMapPrice}
            setPlanMapPrice={setPlanMapPrice}
            selectedCurrency={tenantInformationFormik?.values?.currency}
            tenantCurrencies={tenantCurrencies}
            merchantDetails={merchantDetails}
            isEditing={isEditing}
          />
        );
      index++;
    }

    // PAYCENTRAL FLOW
    if (isPaycentral) {
      if (step === index)
        return (
          <OrganizationInfoStep
            formik={organizationInfoFormik}
            qbotDomain={tenantInformationFormik}
            countryData={countryData}
            stateData={stateData}
            getStateData={getStateData}
            dateFormatOptions={dateFormatOptions}
            handleDateFormatChange={handleDateFormatChange}
            isEditing={isEditing}
            organizationDataById={isCloneTenant ? null : id}
            buttonColor={buttonColor}
            isButtonDisabled={isButtonDisabled}
            timeFormatOptions={timeFormatOptions}
            handleTimeFormatChange={handleTimeFormatChange}
            handleTimeZoneChange={handleTimeZoneChange}
            timeZone={timeZone}
            businessType={businessType}
            getBusinessStateDate={getBusinessStateData}
            businessStateData={businessStateData}
            handleEditing={handleEditing}
            isPaycentralEdit={isPaycentralEdit}
            setIsPaycentralEdit={setIsPaycentralEdit}
            isTrialPortalCreation={isPaycentralTrial}
          // timeZoneData={timeZone}
          />
        );
      index++;

      if (step === index)
        return (
          <CostInfoStep
            formik={costInfoFormik}
            isEditing={isEditing}
            organizationDataById={isCloneTenant ? null : merchantDetails}
          />
        );
      index++;
    }

    if (isKyc) {
      if (step === index)
        return (
          <KycInfoStep
            formik={kycFormik}
            tenantId={isCloneTenant ? null : id}
            countryList={kycCountryList}
            tenantCurrencies={tenantCurrencies}
            qbotDomain={tenantInformationFormik}
            paycentralDomain={organizationInfoFormik}
            isTrialPortalCreation={isKycTrial}
          />
        );
      index++;
    }

    // TRIPOLA FLOW
    if (isTripola) {
      if (step === index)
        return (
          <TripolaInformationStep
            tenantId={isCloneTenant ? null : id}
            formik={tripolaFormik}
            handleEditing={handleEditing}
            isEditing={isEditing}
            countryData={countryData}
            stateData={stateData}
            merchantDetails={merchantDetails}
            tenantCurrencies={tenantCurrencies}
            setIsTripolaEdit={setIsTripolaEdit}
            isTrialPortalCreation={isTripolaTrial}
          />
        );
      index++;

      if (step === index)
        return (
          <TripolaPlanStructureStep
            tenantId={isCloneTenant ? null : id}
            formik={tripolaPlanStructureFormik}
            merchantDetails={merchantDetails}
            isEditing={isEditing}
          />
        );
      index++;
    }

    if (isQbot) {
      if (step === index) {
        return (
          <TenantCreationPreview
            type="qbot"
            previewData={{
              common: commonFormik?.values || {},
              qbot: tenantInformationFormik?.values || {},
              qbotPlan: merchantPlanStructureFormik?.values || {},
            }}
            planMapPrice={planMapPrice}
            selectedCurrency={tenantInformationFormik?.values?.currency}
            tenantCurrencies={tenantCurrencies}
            merchantDetails={merchantDetails}
            isPlanChanged={
              id &&
              merchantDetails?.planId &&
              String(merchantDetails.planId) !==
              String(merchantPlanStructureFormik?.values?.planId)
            }
          />
        );
      }
      index++;
    }
    if (isPaycentral) {
      if (step === index) {
        return (
          <TenantCreationPreview
            type="paycentral"
            previewData={{
              common: commonFormik?.values || {},
              paycentral: organizationInfoFormik?.values || {},
              paycentralCost: costInfoFormik?.values || {},
            }}
            tenantCurrencies={tenantCurrencies}
          />
        );
      }
      index++;
    }
    if (isKyc) {
      if (step === index) {
        return (
          <TenantCreationPreview
            type="kyc"
            previewData={{
              common: commonFormik?.values || {},
              kyc: kycFormik?.values || {},
            }}
            kycCountryList={kycCountryList}
            kycPlanList={kycPlanList}
          />
        );
      }
      index++;
    }
    if (isTripola) {
      if (step === index) {
        return (
          <TenantCreationPreview
            type="tripola"
            previewData={{
              common: commonFormik?.values || {},
              tripola: tripolaFormik?.values || {},
              tripolaPlan: tripolaPlanStructureFormik?.values || {},
            }}
            tripolaPlanList={tripolaPlanList}
          />
        );
      }
      index++;
    }

    return null;
  };

  const touchAllFields = (formik) => {
    const touched = {};
    Object.keys(formik.values).forEach((key) => {
      touched[key] = true;
    });
    formik.setTouched(touched, true);
  }

  // ================= VALIDATION =================

  const isStepValid = async () => {
    const current = steps[activeStep];

    switch (current) {
      case "Basic Info":
        touchAllFields(commonFormik);

        const errors = await commonFormik.validateForm();

        if (Object.keys(errors).length > 0) {
          commonFormik.setErrors(errors);
          return false;
        }

        if (!commonFormik?.values?.isQbot && !commonFormik?.values?.isPaycentral && !id && !commonFormik?.values?.isKyc && !commonFormik?.values?.isTripola) {
          commonFormik.setFieldTouched("atLeastOneProduct", true, false);
          commonFormik.setFieldError("atLeastOneProduct", "Please select at least one product");

          return false;
        }

        return true;

      case "Qbot Information":
        touchAllFields(tenantInformationFormik);

        const tenantErrors = await tenantInformationFormik.validateForm();
        // return Object.keys(tenantErrors).length === 0;
        if (Object.keys(tenantErrors).length > 0) {
          return false;
        }

        if (!tenantInformationFormik.values.isVerified) {
          setSnackbar({
            open: true,
            message: "Please verify domain before proceeding",
            severity: "error",
          });
          return false;
        }

        if (
          tenantInformationFormik.values.isWhiteListIp &&
          (!tenantInformationFormik.values.whitelistIps ||
            tenantInformationFormik.values.whitelistIps.length === 0)
        ) {
          setSnackbar({
            open: true,
            message: "Please add at least one IP address",
            severity: "error",
          });
          return false;
        }
        return true;

      case "Qbot Plan Structure":
        touchAllFields(merchantPlanStructureFormik);

        const planErrors = await merchantPlanStructureFormik.validateForm();
        return Object.keys(planErrors).length === 0;

      case "Corporation Information":
        touchAllFields(organizationInfoFormik);

        const orgErrors = await organizationInfoFormik.validateForm();
        if (isEditMode) {
          delete orgErrors.domainUrl;
          delete orgErrors.userName;
        }

        return Object.keys(orgErrors).length === 0;

      case "Corporation Plan Structure":
        touchAllFields(costInfoFormik);

        const costErrors = await costInfoFormik.validateForm();
        return Object.keys(costErrors).length === 0;

      case "KYC Information":
        touchAllFields(kycFormik);

        const kycErrors = await kycFormik.validateForm();

        if (Object.keys(kycErrors).length > 0) {
          kycFormik.setErrors(kycErrors);
          return false;
        }

        if (!kycFormik.values.kycIsVerified) {
          setSnackbar({
            open: true,
            message: "Please verify your domain before proceeding",
            severity: "error",
          });
          return false;
        }

        return true;

      case "Tripola Information":
        touchAllFields(tripolaFormik);

        const tripolaErrors = await tripolaFormik.validateForm();
        return Object.keys(tripolaErrors).length === 0;

      case "Tripola Plan Structure":
        touchAllFields(tripolaPlanStructureFormik);

        const tripolaPlanErrors = await tripolaPlanStructureFormik.validateForm();
        return Object.keys(tripolaPlanErrors).length === 0;

      default:
        return true;
    }
  };

  // ================= NEXT =================
  const handleNext = async () => {
    const valid = await isStepValid();
    if (!valid) {
      setSnackbar({
        open: true,
        message: "Please complete required fields",
        severity: "error",
      });
      return;
    }

    setActiveStep((prev) => prev + 1);
  };

  // ================= BACK =================
  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleStepClick = async (index) => {
    if (index <= activeStep) {
      setActiveStep(index);
      return;
    }

    if (index > activeStep + 1) {
      return;
    }

    const valid = await isStepValid();

    if (!valid) {
      setSnackbar({
        open: true,
        message: "Please complete required fields",
        severity: "error",
      });
      return;
    }

    setActiveStep(index);
  };

  const handleEditing = (edit) => {
    setIsEditing(edit);
  };

  // Helper: Compare old vs new, return only changed fields with old & new values
  const getChangedFields = (
    oldData,
    newData
  ) => {

    const changes = {};

    if (!oldData) return changes;

    Object.keys(newData).forEach((key) => {

      const oldVal = oldData[key];
      const newVal = newData[key];

      // =========================
      // SKIP undefined both
      // =========================
      if (
        oldVal === undefined &&
        newVal === undefined
      ) {
        return;
      }

      // =========================
      // HANDLE FILE FIELDS
      // =========================
      const isFileField =
        key.toLowerCase().includes("logo") ||
        key.toLowerCase().includes("image") ||
        key === "imagePreview";

      if (isFileField) {

        const oldFile =
          typeof oldVal === "string"
            ? oldVal.split("/").pop()
            : oldVal?.name || "";

        const newFile =
          typeof newVal === "string"
            ? newVal.split("/").pop()
            : newVal?.name || "";

        // Skip if same image
        if (oldFile === newFile) {
          return;
        }

        changes[key] = {
          old: oldVal,
          new: newVal,
        };

        return;
      }

      // =========================
      // HANDLE BOOLEAN FIELDS
      // =========================
      if (
        typeof oldVal === "boolean" ||
        typeof newVal === "boolean"
      ) {

        if (
          Boolean(oldVal) ===
          Boolean(newVal)
        ) {
          return;
        }

        changes[key] = {
          old: oldVal,
          new: newVal,
        };

        return;
      }

      // =========================
      // HANDLE OBJECTS
      // =========================

      if (
        typeof oldVal === "object" &&
        typeof newVal === "object" &&
        !Array.isArray(oldVal) &&
        !Array.isArray(newVal) &&
        oldVal !== null &&
        newVal !== null
      ) {

        if (
          JSON.stringify(oldVal) ===
          JSON.stringify(newVal)
        ) {
          return;
        }

        changes[key] = {
          old: oldVal,
          new: newVal,
        };

        return;
      }

      // =========================
      // HANDLE ARRAYS
      // =========================
      if (
        Array.isArray(oldVal) ||
        Array.isArray(newVal)
      ) {

        if (
          JSON.stringify(oldVal || []) ===
          JSON.stringify(newVal || [])
        ) {
          return;
        }

        changes[key] = {
          old: oldVal,
          new: newVal,
        };

        return;
      }

      // =========================
      // HANDLE NORMAL VALUES
      // =========================
      const oldValue =
        String(oldVal || "").trim();

      const newValue =
        String(newVal || "").trim();

      // Skip unchanged
      if (oldValue === newValue) {
        return;
      }

      changes[key] = {
        old: oldVal,
        new: newVal,
      };

    });

    return changes;
  };

  const isValueChanged = (
    oldValue,
    newValue
  ) => {

    // Handle booleans properly
    if (
      typeof oldValue === "boolean" ||
      typeof newValue === "boolean"
    ) {
      return (
        Boolean(oldValue) !==
        Boolean(newValue)
      );
    }

    // Handle null undefined empty
    return (
      String(oldValue || "").trim() !==
      String(newValue || "").trim()
    );
  };

  const getAllChangedFields = () => {
    if (!isEditMode) return {};

    const pickValues = (source, template) => {
      const result = {};
      Object.keys(template).forEach(key => {
        result[key] = source[key];
      });
      return result;
    };

    const changed = {};



    // --- Common (Basic Info) ---
    if (tenantData) {
      const commonOld = {
        firstName: tenantData.firstName || "",
        lastName: tenantData.lastName || "",
        email: tenantData.email || "",
        companyPhoneNumber: tenantData.phoneNumber || "",
        companyCountry: tenantData.country || "",
        companyState: tenantData.state || "",
        companyAddress: tenantData.address || "",
        isQbot: tenantData?.assignPortal?.isQbot ?? false,
        isPaycentral: tenantData?.assignPortal?.isPaycentral ?? false,
        isKyc: tenantData?.assignPortal?.isKyc ?? false,
        isTripola: tenantData?.assignPortal?.isTripola ?? false,
        isTrialConfig: tenantData.hasTrialConfiguration ?? false,
        trialPortal: Array.isArray(tenantData.trialPortal) ? tenantData.trialPortal : (tenantData.trialPortal ? [tenantData.trialPortal] : []),
        trialDays: tenantData.trialDays ?? null,
        graceDays: tenantData.graceDays ?? null,
      };

      const commonNew = commonFormik.values;
      const commonChanges = getChangedFields(commonOld, commonNew);
      if (Object.keys(commonChanges).length > 0) {
        changed.common = commonChanges;
      }
    }

    // --- QBOT (Tenant Information) ---
    if (originalQbotData && commonFormik.values.isQbot) {
      const qbotOld = {
        companyName: originalQbotData.companyName || "",
        currency: originalQbotData.currency || "",
        companyEmail: originalQbotData.companyEmail || "",
        companyZipCode: originalQbotData.companyZipCode || "",
        domainUrl: originalQbotData.domainUrl || "",
        // isVerified: originalQbotData.isVerified ?? false,
        isWhiteListIp: originalQbotData.ipRestriction ?? false,
        whitelistIps:
          typeof originalQbotData.whitelistIps === "string"
            ? JSON.parse(originalQbotData.whitelistIps || "[]")
            : originalQbotData.whitelistIps || [],
        brandingName: originalQbotData.brandingName || "",
        brandingLogo: originalQbotData.brandingLogo || "",
        companyAddress: originalQbotData.companyAddress || "",
        companyPhoneNumber: originalQbotData.companyPhoneNumber || "",
      };

      const qbotNew = pickValues(tenantInformationFormik.values, qbotOld);
      const qbotChanges = getChangedFields(qbotOld, qbotNew);
      if (Object.keys(qbotChanges).length > 0) {
        changed.qbot = qbotChanges;
      }
    }

    const stableStringify = (obj) => {
      if (!obj || typeof obj !== "object") {
        return JSON.stringify(obj);
      }

      return JSON.stringify(
        Object.keys(obj)
          .sort()
          .reduce((acc, key) => {
            acc[key] = obj[key];
            return acc;
          }, {})
      );
    };

    // --- QBOT Plan Structure ---
    if (originalQbotData && commonFormik.values.isQbot) {
      const qbotPlanOld = {
        planId: originalQbotData.planId || null,
        billingCycle: originalQbotData.planHistory?.[0]?.billingCycle || "",
        paymentMode: originalQbotData?.paymentMode || "",
        isAutoPay: originalQbotData.isAutoPay ?? true,
        // isCustomPlan: true,
        allowedFeatures: stableStringify(
          typeof originalQbotData.allowedFeatures === "string"
            ? JSON.parse(originalQbotData.allowedFeatures || "{}")
            : originalQbotData.allowedFeatures || {}
        ),
        hideBillingDetails: false,
      };

      const qbotPlanNew = {
        ...pickValues(merchantPlanStructureFormik.values, qbotPlanOld),
        allowedFeatures: stableStringify(merchantPlanStructureFormik.values.allowedFeatures || {}),
      };
      const qbotPlanChanges = getChangedFields(qbotPlanOld, qbotPlanNew);
      if (Object.keys(qbotPlanChanges).length > 0) {
        changed.qbotPlan = qbotPlanChanges;
      }
    }

    // --- PayCentral (Organization Info) ---
    if (originalPaycentralData && commonFormik.values.isPaycentral) {
      const orgOld = {
        organizationLogo:
          originalPaycentralData.organizationLogo || "",

        imagePreview:
          originalPaycentralData.organizationLogo || "",

        userName:
          originalPaycentralData.userName || "",

        businessName:
          originalPaycentralData.businessName || "",

        businessType:
          originalPaycentralData.businessType || "",

        businessDescription:
          originalPaycentralData.businessDescription || "",

        businessCountry:
          originalPaycentralData.businessCountry || "",

        businessState:
          originalPaycentralData.businessState || "",

        domainUrl:
          originalPaycentralData.domainUrl || "",

        organizationName:
          originalPaycentralData.organizationName || "",

        website:
          originalPaycentralData.websiteURL || "",

        taxId:
          originalPaycentralData.taxId || "",

        dateFormat:
          originalPaycentralData.dateFormat || "",

        timeFormat:
          originalPaycentralData.timeFormat || "",

        timeZone:
          originalPaycentralData.timeZone || "",

        isAutoInvoice:
          originalPaycentralData.isAutoInvoice ?? false,

        isActive:
          originalPaycentralData.isActive ?? true,

        isVerified: true,
      };

      const orgNewRaw = {
        ...organizationInfoFormik.values,
        domainUrl: organizationInfoFormik.values.paycentralDomainUrl || organizationInfoFormik.values.domainUrl || "",
      };

      const orgNew = pickValues(orgNewRaw, orgOld);
      const orgChanges = getChangedFields(orgOld, orgNew);
      if (Object.keys(orgChanges).length > 0) {
        changed.paycentral = orgChanges;
      }
    }

    // --- Cost Info ---
    if (originalPaycentralData && commonFormik.values.isPaycentral) {
      const parsedCost = safeJsonParse(originalPaycentralData.costInfo) || {};
      const costOld = {
        oneTimeSetupFee: parsedCost.oneTimeSetupFee || "",
        ongoingMaintenanceFee: parsedCost.ongoingMaintenanceFee || "",
        newMerchantSetupFee: parsedCost.newMerchantSetupFee || "",
        ongoingMaintenancePerMerchantFee: parsedCost.ongoingMaintenancePerMerchantFee || "",
      };

      const costNew = pickValues(costInfoFormik.values, costOld);
      const costChanges = getChangedFields(costOld, costNew);
      if (Object.keys(costChanges).length > 0) {
        changed.paycentralCost = costChanges;
      }
    }

    // --- KYC ---
    if (commonFormik.values.isKyc) {
      // You need to fetch original KYC data for proper comparison
      // For now comparing against initial formik values
      const kycOld = {
        kycUserName: originalKycData.userName || "",
        kycCompanyName: originalKycData.companyName || "",
        kycCompanyEmail: originalKycData.companyEmail || "",
        kycDomainUrl: originalKycData.domainUrl || "",
        kycCountry: originalKycData.country || "",
        kycPlanId: originalKycData.planId || null,
        kycBillingCycle: originalKycData.BillingDetails || null,
        kycIsAutoPay: originalKycData.isAutoPay ?? true,
        kycPaymentMode: originalKycData.kycPaymentMode || originalKycData.paymentMode || "",
        kycIsVerified: true,
        kycCurrency: originalKycData.currency || "",
      };

      const kycNew = pickValues(kycFormik.values, kycOld);
      const kycChanges = getChangedFields(kycOld, kycNew);
      if (Object.keys(kycChanges).length > 0) {
        changed.kyc = kycChanges;
      }
    }

    // --- Tripola ---
    if (commonFormik.values.isTripola) {
      const tripolaOld = {
        tripolaCompanyName: originalTripolaData.companyName || "",
        tripolaCompanyEmail: originalTripolaData.companyEmail || "",
        tripolaCompanyZipCode: originalTripolaData.companyZipCode || "",
        tripolaCompanyCity: originalTripolaData.companyCity || "",
        tripolaCompanyPhoneNumber: originalTripolaData.companyPhoneNumber || "",
        tripolaDomainUrl: originalTripolaData.domainUrl || "",
        tripolaIsVerified: true,
        tripolaCurrency: originalTripolaData.currency || "",
      };

      const tripolaNew = pickValues(tripolaFormik.values, tripolaOld);
      const tripolaChanges = getChangedFields(tripolaOld, tripolaNew);
      if (Object.keys(tripolaChanges).length > 0) {
        changed.tripola = tripolaChanges;
      }
    }

    // --- Tripola Plan ---
    if (commonFormik.values.isTripola) {
      const tripolaPlanOld = {
        tripolaPlanId: originalTripolaData.planId || null,
        tripolaBillingCycle:
          originalTripolaData.billingCycle || "",
        tripolaIsAutoPay:
          originalTripolaData.isAutoPay ?? true,
      };

      const tripolaPlanNew = pickValues(tripolaPlanStructureFormik.values, tripolaPlanOld);
      const tripolaPlanChanges = getChangedFields(tripolaPlanOld, tripolaPlanNew);
      if (Object.keys(tripolaPlanChanges).length > 0) {
        changed.tripolaPlan = tripolaPlanChanges;
      }
    }

    // Format names for currency and plans
    const formatCurrencyName = (val) => {
      if (!val) return val;
      const curr = tenantCurrencies?.find(c => String(c.id) === String(val));
      return curr ? curr.name : val;
    };

    const formatQbotPlanName = (val) => {
      if (!val) return val;
      return planMapPrice?.[val]?.name || val;
    };

    const formatKycPlanName = (val) => {
      if (!val) return val;
      const p = kycPlanList?.find(c => String(c.id) === String(val));
      return p ? p.name : val;
    };

    const formatTripolaPlanName = (val) => {
      if (!val) return val;
      const p = tripolaPlanList?.find(c => String(c.id) === String(val));
      return p ? p.plan_name : val;
    };

    // Apply formatting
    if (changed.qbot?.currency) {
      if (changed.qbot.currency.old) changed.qbot.currency.old = formatCurrencyName(changed.qbot.currency.old);
      changed.qbot.currency.new = formatCurrencyName(changed.qbot.currency.new);
    }
    if (changed.kyc?.kycCurrency) {
      if (changed.kyc.kycCurrency.old) changed.kyc.kycCurrency.old = formatCurrencyName(changed.kyc.kycCurrency.old);
      changed.kyc.kycCurrency.new = formatCurrencyName(changed.kyc.kycCurrency.new);
    }
    if (changed.tripola?.tripolaCurrency) {
      if (changed.tripola.tripolaCurrency.old) changed.tripola.tripolaCurrency.old = formatCurrencyName(changed.tripola.tripolaCurrency.old);
      changed.tripola.tripolaCurrency.new = formatCurrencyName(changed.tripola.tripolaCurrency.new);
    }
    if (changed.qbotPlan?.planId) {
      changed.qbotPlan.planName = { ...changed.qbotPlan.planId };
      delete changed.qbotPlan.planId;
      if (changed.qbotPlan.planName.old) changed.qbotPlan.planName.old = formatQbotPlanName(changed.qbotPlan.planName.old);
      changed.qbotPlan.planName.new = formatQbotPlanName(changed.qbotPlan.planName.new);
    }
    if (changed.kyc?.kycPlanId) {
      changed.kyc.kycPlanName = { ...changed.kyc.kycPlanId };
      delete changed.kyc.kycPlanId;
      if (changed.kyc.kycPlanName.old) changed.kyc.kycPlanName.old = formatKycPlanName(changed.kyc.kycPlanName.old);
      changed.kyc.kycPlanName.new = formatKycPlanName(changed.kyc.kycPlanName.new);
    }
    if (changed.tripolaPlan?.tripolaPlanId) {
      changed.tripolaPlan.tripolaPlanName = { ...changed.tripolaPlan.tripolaPlanId };
      delete changed.tripolaPlan.tripolaPlanId;
      if (changed.tripolaPlan.tripolaPlanName.old) changed.tripolaPlan.tripolaPlanName.old = formatTripolaPlanName(changed.tripolaPlan.tripolaPlanName.old);
      changed.tripolaPlan.tripolaPlanName.new = formatTripolaPlanName(changed.tripolaPlan.tripolaPlanName.new);
    }

    return changed;
  };


  const extractOnlyNewValues = (changedFields) => {
    const finalData = {};

    Object.keys(changedFields || {}).forEach((section) => {
      finalData[section] = {};

      Object.keys(changedFields[section]).forEach((field) => {
        finalData[section][field] = changedFields[section][field].new;
      });
    });

    return finalData;
  };

  // ================= FINAL SUBMIT =================
  const handleFinish = async () => {
    const valid = await isStepValid();
    if (!valid) {
      setSnackbar({
        open: true,
        message: "Please complete required fields",
        severity: "error",
      });

      return;
    }
    setLoading(true);

    const formData = new FormData();

    const ipRestriction = tenantInformationFormik.values.isWhiteListIp === true;

    const allValues = {
      ...(commonFormik?.values || {}),
      ...(commonFormik?.values?.isQbot ||
        commonFormik?.values?.trialPortal?.includes("qbot")
        ? tenantInformationFormik?.values
        : {}),
      ...(commonFormik?.values?.isQbot ||
        commonFormik?.values?.trialPortal?.includes("qbot")
        ? merchantPlanStructureFormik?.values
        : {}),
      ...(commonFormik?.values?.isPaycentral ||
        commonFormik?.values?.trialPortal?.includes("paycentral")
        ? (() => {
          const { domainUrl, ...rest } = organizationInfoFormik.values;

          return {
            ...rest,
            paycentralDomainUrl: domainUrl,
          };
        })()
        : {}),
      ...(commonFormik?.values?.isKyc ||
        commonFormik?.values?.trialPortal?.includes("kyc")
        ? kycFormik?.values
        : {}),
      ...(commonFormik?.values?.isTripola ||
        commonFormik?.values?.trialPortal?.includes("tripola")
        ? tripolaFormik?.values
        : {}),
      ...(commonFormik?.values?.isTripola ||
        commonFormik?.values?.trialPortal?.includes("tripola")
        ? tripolaPlanStructureFormik?.values
        : {}),
    };

    if (!allValues.whitelistIps) {
      allValues.whitelistIps = [];
    }

    // formData.append(
    //   "selectedProducts",
    //   JSON.stringify({
    //     qbot: commonFormik?.values?.isQbot,
    //     paycentral: commonFormik?.values?.isPaycentral,
    //     kyc: commonFormik?.values?.isKyc,
    //     tripola: commonFormik?.values?.isTripola
    //   })
    // );
    formData.append(
      "selectedProducts",
      JSON.stringify({
        qbot:
          commonFormik.values.isQbot ||
          commonFormik.values.trialPortal?.includes("qbot"),

        paycentral:
          commonFormik.values.isPaycentral ||
          commonFormik.values.trialPortal?.includes("paycentral"),

        kyc:
          commonFormik.values.isKyc ||
          commonFormik.values.trialPortal?.includes("kyc"),

        tripola:
          commonFormik.values.isTripola ||
          commonFormik.values.trialPortal?.includes("tripola"),
      })
    );

    formData.append(
      "isTrialConfig",
      commonFormik.values.isTrialConfig
    );

    formData.append(
      "trialPortal",
      JSON.stringify(commonFormik.values.trialPortal || [])
    );

    formData.append("slaName", commonFormik.values.slaName);

    formData.append("currentStep", activeStep);
    formData.append("isImageEdited", isEditing);
    formData.append("editFlags", JSON.stringify({ isQbotEdit, isPaycentralEdit, isTripolaEdit }));

    formData.append("ipRestriction", ipRestriction);

    formData.append(
      "whitelistIps",
      ipRestriction
        ? JSON.stringify(tenantInformationFormik.values.whitelistIps || [])
        : JSON.stringify([])
    );

    if (commonFormik?.values?.isPaycentral ||
      commonFormik?.values?.trialPortal?.includes("paycentral")) {
      formData.append(
        "costInfo",
        JSON.stringify(costInfoFormik?.values)
      );
    }

    const brandingLogo = tenantInformationFormik?.values?.brandingLogo?.originFileObj ||
      tenantInformationFormik?.values?.brandingLogo;

    if (brandingLogo instanceof File) {
      formData.append("brandingLogo", brandingLogo);
    }

    const logo = organizationInfoFormik?.values?.organizationLogo?.originFileObj ||
      organizationInfoFormik?.values?.organizationLogo;

    if (logo instanceof File) {
      formData.append("organizationLogo", logo);
    }

    const tripolaLogo = tripolaFormik?.values?.tripolaCompanyLogo?.originFileObj ||
      tripolaFormik?.values?.tripolaCompanyLogo;

    if (tripolaLogo instanceof File) {
      formData.append("tripolaCompanyLogo", tripolaLogo);
    }

    Object.entries(allValues).forEach(([key, value]) => {
      if (
        key === "brandingLogo" ||
        key === "organizationLogo" ||
        key === "tripolaCompanyLogo"
      ) {
        return;
      }

      if (value instanceof File) {
        formData.append(key, value);
      } else if (typeof value === "object" && value !== null) {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value ?? "");
      }
    });

    let finalValuesToSend = allValues;
    if (isEditMode) {
      const allChangedFields = getAllChangedFields();
      const updatePayload = extractOnlyNewValues(allChangedFields);

      const allKeys = [];
      Object.values(allChangedFields).forEach(portalChanges => {
        allKeys.push(...Object.keys(portalChanges));
      });

      formData.append("changedFields", JSON.stringify(allKeys));
      formData.append("comparisonData", JSON.stringify(allChangedFields));
      formData.append(
        "updatePayload",
        JSON.stringify(updatePayload)
      );
      const flattenPayload = (obj) => {
        const finalObj = {};

        Object.values(obj || {}).forEach((section) => {
          Object.assign(finalObj, section);
        });

        return finalObj;
      };

      // Object.entries(finalValuesToSend).forEach(([key, value]) => {
      //   if (key === "brandingLogo" || key === "organizationLogo" || key === "tripolaCompanyLogo") return;
      //   if (value instanceof File) {
      //     formData.append(key, value);
      //   } else if (typeof value === "object" && value !== null) {
      //     formData.append(key, JSON.stringify(value));
      //   } else {
      //     formData.append(key, value ?? "");
      //   }
      // });
    }

    try {
      let response;

      if (isEditMode) {
        response = await editTenantAction(id, formData);
      } else {
        for (const [key, value] of formData.entries()) {
        }
        response = await createTenantAction(formData);
      }

      // Handle all success statuses including approval flow
      if (
        response?.success ||
        response?.status === 200 ||
        response?.status === 201 ||
        response?.status === 202
      ) {
        navigate("/tenant", {
          state: {
            snackbar: {
              message: response?.message || "Request submitted successfully",
              severity: "success",
            },
          },
        });
      } else {
        setSnackbar({
          open: true,
          message:
            response?.message ||
            response?.data?.message ||
            "Something went wrong",
          severity: "error",
        });
      }
    } catch (e) {
      setSnackbar({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/tenant");
  };

  const ActiveStepIcon = STEP_ICON_MAP[steps[activeStep]] || UserRoundPlus;

  if (pageLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          bgcolor: "#f8fafc",
          minHeight: "60vh",
          p: { xs: 2, md: 3 },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 420,
            px: 4,
            py: 5,
            textAlign: "center",
            borderRadius: 3,
            border: "1px solid #e2e8f0",
            background: "#ffffff",
            boxShadow: "0 18px 40px -28px rgba(15, 23, 42, 0.28)",
          }}
        >
          <CircularProgress
            size={40}
            thickness={4}
            sx={{ color: "#22419e", mb: 2.5 }}
          />
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 700,
              color: "#0f172a",
              letterSpacing: "-0.02em",
            }}
          >
            Loading tenant details
          </Typography>
          <Typography
            sx={{
              mt: 1,
              fontSize: 13,
              lineHeight: 1.5,
              color: "#64748b",
            }}
          >
            Fetching account information and portal configuration…
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <div className="ts-page">
      <div className="ts-shell">
        <div className="ts-track-card">
          <div className="ts-track-meta">
            <div className="ts-track-meta-left">
              <strong>
                Step {activeStep + 1} of {steps.length}
              </strong>
              <span>{steps[activeStep]}</span>
            </div>
            <div className="ts-progress-pill" aria-hidden>
              <i>
                <em
                  style={{
                    width: `${Math.round(
                      ((activeStep + 1) / Math.max(steps.length, 1)) * 100
                    )}%`,
                  }}
                />
              </i>
              {Math.round(
                ((activeStep + 1) / Math.max(steps.length, 1)) * 100
              )}
              %
            </div>
          </div>

          <div className="ts-track">
            {steps?.map((step, index) => {
              const isActive = activeStep === index;
              const isCompleted = activeStep > index;
              const isClickable = index <= activeStep;
              const isLast = index === steps.length - 1;

              return (
                <div
                  key={step}
                  className={`ts-step-wrap${isLast ? " is-last" : ""}`}
                >
                  <button
                    type="button"
                    className={`ts-step${isActive ? " is-active" : ""}${
                      isCompleted ? " is-completed" : ""
                    }${isClickable ? " is-clickable" : ""}`}
                    onClick={() => isClickable && handleStepClick(index)}
                    title={step}
                    aria-current={isActive ? "step" : undefined}
                    disabled={!isClickable}
                  >
                    <span className="ts-step-marker" aria-hidden>
                      {isCompleted ? (
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        index + 1
                      )}
                    </span>
                    <p className="ts-step-label">{step}</p>
                  </button>

                  {!isLast ? (
                    <div
                      className={`ts-connector${
                        activeStep > index ? " is-done" : ""
                      }`}
                      aria-hidden
                    >
                      <span />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>

        <div className="ts-form-card">
          <div className="ts-form-head">
            <div className="ts-form-head-icon" aria-hidden>
              <ActiveStepIcon size={22} strokeWidth={2} color="#ffffff" />
            </div>
            <div className="ts-form-head-copy">
              <h2>{steps[activeStep]}</h2>
              <p>{stepDescriptions[steps[activeStep]]}</p>
            </div>
            <span className="ts-form-head-step">
              {activeStep + 1} / {steps.length}
            </span>
          </div>

          <Box className="ts-form-body mp-stepper-form" sx={stepperFormScopeSx}>
            <Fade key={activeStep} in timeout={280}>
              <Box sx={{ width: "100%" }}>{getStepContent(activeStep)}</Box>
            </Fade>
          </Box>

          <div className="ts-form-foot">
            {activeStep === 0 ? (
              <button
                type="button"
                className="ts-btn"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
            ) : (
              <button
                type="button"
                className="ts-btn"
                onClick={handleBack}
                disabled={loading}
              >
                Back
              </button>
            )}

            <button
              type="button"
              className="ts-btn is-primary"
              onClick={
                activeStep === steps.length - 1 ? handleFinish : handleNext
              }
              disabled={loading}
            >
              {activeStep === steps.length - 1 ? "Finish" : "Next"}
              {loading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : null}
            </button>
          </div>
        </div>
      </div>

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
    </div>
  );
};

export default AddEditTenant;